import { create } from 'zustand';
import toolsService from '../services/toolsService';
import { useAuthStore } from './authStore';

/**
 * Per-user tool usage counts (slug → count), loaded from /api/tool-usage/most-used.
 * Bumped optimistically when recordToolUsage fires.
 */
const POPULAR_TTL_MS = 10 * 60 * 1000; // the server caches it for 10 minutes too
let popularFetchedAt = 0;
let totalsFetchedAt = 0;

export const useToolUsageStore = create((set, get) => ({
    counts: {},
    loaded: false,
    // Everyone's usage (slug → runs in the last 30 days) — only used to mark the "Hot" tools.
    popular: {},
    popularLoaded: false,

    // Everyone's ALL-TIME runs per tool (slug → count) — the "used N times" on a tool's own page.
    // (`counts` above is only THIS user's own usage and drives the menu/tools ordering.)
    totals: {},
    totalsLoaded: false,

    getCount: (slug) => get().counts[slug] ?? null,

    loadTotals: async () => {
        if (!useAuthStore.getState().token) return;
        if (Date.now() - totalsFetchedAt < POPULAR_TTL_MS) return;
        totalsFetchedAt = Date.now();
        try {
            const rows = await toolsService.getTotals();
            const totals = {};
            for (const row of rows || []) {
                if (row.toolSlug && row.count) totals[row.toolSlug] = row.count;
            }
            set({ totals, totalsLoaded: true });
        } catch {
            totalsFetchedAt = 0; // try again next time
        }
    },

    load: async () => {
        if (!useAuthStore.getState().token) {
            set({ loaded: true });
            return;
        }
        try {
            const rows = await toolsService.getMostUsed(50);
            const counts = {};
            for (const row of rows || []) {
                const key = row.toolSlug || row.slug;
                const count = row.count ?? row.useCount ?? row.total;
                if (key && count) counts[key] = count;
            }
            set({ counts, loaded: true });
        } catch {
            set({ loaded: true });
        }
    },

    loadPopular: async () => {
        if (!useAuthStore.getState().token) return;
        if (Date.now() - popularFetchedAt < POPULAR_TTL_MS) return;
        popularFetchedAt = Date.now();
        try {
            const rows = await toolsService.getPopular();
            const popular = {};
            for (const row of rows || []) {
                if (row.toolSlug && row.count) popular[row.toolSlug] = row.count;
            }
            set({ popular, popularLoaded: true });
        } catch {
            popularFetchedAt = 0; // try again next time
        }
    },

    bump: (slug) => {
        if (!slug) return;
        set((state) => ({
            counts: {
                ...state.counts,
                [slug]: (state.counts[slug] ?? 0) + 1,
            },
            // Keep the page counter in step with a run you just did (only once totals have loaded;
            // a tool nobody has used yet is absent from them, so it starts from 0).
            totals: state.totalsLoaded
                ? { ...state.totals, [slug]: (state.totals[slug] ?? 0) + 1 }
                : state.totals,
        }));
    },
}));
